import { API_LABEL, type ApiKind } from "./model";
import { metricsColumn, type MetricsSort, type MetricsSortColumn } from "./metricsColumns";
import type { MetricsFilters, PartnerMetricsRow } from "./metrics.types";

/**
 * Pure filter and sort transforms for the Metrics Dashboard. Metrics insight
 * calculation and display projection live behind `metricsInsight.ts`.
 */

export const DEFAULT_FILTERS: MetricsFilters = {
  partnerId: "all",
  tierId: "all",
  region: "all",
  api: "all",
  status: "all",
};

/**
 * Default sort — overage descending. Contract breaches rise to the top;
 * partners with no overage naturally sort last. (POC UX review, Aug 19.)
 */
export const DEFAULT_SORT: MetricsSort = { column: "overage", direction: "desc" };

/** True when every filter is at its default ("all") — drives the filter bar's Reset button. */
export function isDefaultFilters(filters: MetricsFilters): boolean {
  return (Object.keys(DEFAULT_FILTERS) as (keyof MetricsFilters)[]).every(
    (key) => filters[key] === DEFAULT_FILTERS[key]
  );
}

export function applyFilters(
  rows: PartnerMetricsRow[],
  filters: MetricsFilters
): PartnerMetricsRow[] {
  return rows.filter((row) => {
    if (filters.partnerId !== "all" && row.id !== filters.partnerId) return false;
    if (filters.tierId !== "all" && row.partner.tierId !== filters.tierId) return false;
    if (filters.region !== "all" && !row.tier.regions[filters.region]) return false;
    if (filters.api !== "all" && !row.apiBreadth.includes(API_LABEL[filters.api as ApiKind])) {
      return false;
    }
    if (filters.status !== "all" && row.status !== filters.status) return false;
    return true;
  });
}

/** Non-mutating sort — returns a new array. */
export function sortRows(rows: PartnerMetricsRow[], sort: MetricsSort): PartnerMetricsRow[] {
  const read = metricsColumn(sort.column).sortValue;
  const factor = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = read(a);
    const bv = read(b);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
}

/** Toggle direction when re-selecting the current column; otherwise use its default. */
export function nextSort(current: MetricsSort, column: MetricsSortColumn): MetricsSort {
  if (current.column === column) {
    return { column, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return { column, direction: metricsColumn(column).sortAscByDefault ? "asc" : "desc" };
}
